import { useCallback, useEffect, useState } from "react";
import { getPermissionData, getUserData } from "../../Actions/common";
import {
  deleteMultipleUsersByIdAction,
  getUserDetailByIdAction,
  getUsersAction,
  unblockUserAction,
  updateUserStatusAction,
} from "../../Actions/UserActions";
import { listAllCommonTenants } from "../../Actions/PaycentralAction";
import { MEDIA_URL } from "../../config";
import CustomSnackbar from "../Common/snackBar";
import AddEditMasterUserModal from "./AddEditUser";

import Avatar from "@mui/material/Avatar";
import { getRoleAction } from "../../Actions/RoleActions";
import BreadcrumbsComponent from "../../components/Common/Breadcrumbs";
import showConfirmation from "../../utils/Conformation";
import DataTable from "../../components/DataTable/DataTable";
import Swal from "sweetalert2";

const MasterUserSection = () => {
  const [masterUserData, setMasterUserData] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [masterUserDataById, setMasterUserDataById] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterValues, setFilterValues] = useState({
    search: "",
    status: "all"
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [shouldFetchData, setShouldFetchData] = useState(1);
  const [roles, setRoles] = useState([]);
  const [merchantUser, setMerchantUser] = useState([]);
  const [selectedRowsPerPage, setSelectedRowsPerPage] = useState(10);

  const userData = localStorage.getItem("userData") ? getUserData() : null;
  const permissionData = localStorage.getItem(`${userData?.id}_permissionData`)
    ? getPermissionData(`${userData?.id}_permissionData`)
    : [];
  const hasPermission = permissionData[0]?.role_permissions?.find(
    (m) => m?.module && m?.module?.slug === "users"
  );

  const getMasterUserData = useCallback(async () => {
    setLoading(true);
    await getUsersAction(
      currentPage,
      filterValues.search,
      selectedRowsPerPage,
      filterValues.status
    )
      .then((res) => {
        const { data, totalRecords } = res?.data;
        setMasterUserData(data.rows);
        setTotalRecords(totalRecords);
        setLoading(false);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to fetch users",
          severity: "error",
        });
        setLoading(false);
      });
  }, [currentPage, filterValues, selectedRowsPerPage]);

  useEffect(() => {
    if (shouldFetchData) {
      getMasterUserData();
      setShouldFetchData(0);
    }
  }, [shouldFetchData, getMasterUserData]);

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddUnitRate = async (e, id) => {
    if (id) {
      await getUserDetailByIdAction(id)
        .then((res) => {
          setMasterUserDataById(res?.data?.data?.data);
          setOpenModal(true);
        })
        .catch((error) => {
          setSnackbar({
            open: true,
            message: error?.response?.data?.message || "Failed to fetch user details",
            severity: "error",
          });
        });
    } else {
      setOpenModal(true);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setMasterUserDataById(null);
    setShouldFetchData(1);
  };

  const handleDeleteUser = async (e, deleteId) => {
    if (e && e.stopPropagation) e.stopPropagation();

    await showConfirmation("simpleDelete", {
      entityName: "User",
      entityId: deleteId,
      action: async (id) => {
        return await deleteMultipleUsersByIdAction(id);
      },
      onSuccess: () => {
        getMasterUserData();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to delete user",
          severity: "error",
        });
      },
      showCancelledMsg: true,
    });
  };

  const changeMasterUserStatus = async (e, ele) => {
    if (e && e.stopPropagation) e.stopPropagation();

    await showConfirmation("toggleStatus", {
      entityName: "User",
      entityId: ele.id,
      currentStatus: ele.isActive,
      action: async ({ id }) => {
        return await updateUserStatusAction(id, ele.isActive);
      },
      onSuccess: (res) => {
        setSnackbar({
          open: true,
          message: res?.data?.message || "Status updated successfully",
          severity: "success",
        });
        getMasterUserData();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to update status",
          severity: "error",
        });
      },
    });
  };

  const getMerchantUser = async () => {
    await listAllCommonTenants(null, null, null, null)
      .then((res) => {
        const { data } = res.data;
        setMerchantUser(data);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to fetch merchants",
          severity: "error",
        });
      });
  };

  const handleBulkAction = async (actionType, ids) => {
    if (actionType === "delete") {
      Swal.fire({
        title: "Deleting Users...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });
      await deleteMultipleUsersByIdAction(ids)
        .then((res) => {
          Swal.fire({
            title: "Deleted!",
            text: res?.data?.message || "Users deleted successfully!",
            icon: "success",
            confirmButtonColor: "#22419e",
          });
          setCurrentPage(1);
          setShouldFetchData(1);
        })
        .catch((error) => {
          Swal.fire({
            title: "Error",
            text: error.response?.data?.message || "Failed to delete selected users",
            icon: "error",
            confirmButtonColor: "#22419e",
          });
        });
    }
  };

  const handleUnblockUser = async (e, userId) => {
    if (e && e.stopPropagation) e.stopPropagation();
    await showConfirmation("customToggle", {
      entityName: "User",
      entityId: userId,
      customTitle: "Unblock User?",
      customText: "Are you sure you want to unblock this user?",
      confirmButtonText: "Yes, Unblock",
      confirmButtonColor: "#22419e",
      isActivating: true,
      action: async (id) => {
        return await unblockUserAction(id);
      },
      onSuccess: (res) => {
        setSnackbar({
          open: true,
          message: res?.data?.message || res?.message || "User unblocked successfully",
          severity: "success",
        });
        getMasterUserData();
      },
      onError: (error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to unblock user",
          severity: "error",
        });
      },
    });
  };

  const fetchRoles = () => {
    getRoleAction("", true)
      .then((res) => {
        const { data } = res.data;
        setRoles(data);
      })
      .catch((error) => {
        setSnackbar({
          open: true,
          message: error.response?.data?.message || "Failed to fetch roles",
          severity: "error",
        });
      });
  };

  useEffect(() => {
    fetchRoles();
    getMerchantUser();
  }, []);

  const stringAvatar = (profileData) => {
    return {
      children: `${profileData?.split("")[0]}`,
    };
  };

  const [imageError, setImageError] = useState(false);

  // Table Columns Definition (excluding checkbox and action columns)
  const columns = [
    {
      field: "firstName",
      renderCell: (params) => {
        const hasLogo = params.row.profilePic && !imageError;
        let firstNameString = "N/A";
        try {
          firstNameString = params.row.firstName ? JSON.parse(params.row.firstName) : "N/A";
        } catch {
          firstNameString = params.row.firstName || "N/A";
        }

        return (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              padding: "0px 0",
            }}
          >
            {hasLogo ? (
              <img
                src={`${MEDIA_URL}/qbotmasterapp.netcluesdemo.com/${params.row.profilePic}`}
                height="40px"
                width="40px"
                alt={`${firstNameString?.charAt(0) || ""}`}
                style={{
                  marginRight: "10px",
                  background: "#bfcfff",
                  objectFit: "cover",
                  borderRadius: "40px",
                  color: "#23419e",
                }}
                onError={() => setImageError(true)}
              />
            ) : (
              <Avatar
                {...stringAvatar(firstNameString)}
                className="h-40px w-40px rounded-5 bg-primary-subtle text-primary mr-5"
                style={{
                  marginRight: "10px",
                }}
              />
            )}
            <span
              style={{
                fontSize: 15,
                fontWeight: 500,
                color: "#333",
              }}
            >
              {firstNameString}
            </span>
          </div>
        );
      },
      minWidth: 220,
      headerName: "First Name",
      flex: 1,
    },
    {
      field: "lastName",
      minWidth: 150,
      headerName: "Last Name",
      flex: 1,
      renderCell: (params) => {
        let lastNameString = "N/A";
        try {
          lastNameString = params.row.lastName ? JSON.parse(params.row.lastName) : "N/A";
        } catch {
          lastNameString = params.row.lastName || "N/A";
        }
        return (
          <span style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>
            {lastNameString}
          </span>
        );
      },
    },
    {
      field: "email",
      minWidth: 150,
      headerName: "Email",
      flex: 1,
      renderCell: (params) => {
        let emailString = "N/A";
        try {
          emailString = params.row.email ? JSON.parse(params.row.email) : "N/A";
        } catch {
          emailString = params.row.email || "N/A";
        }
        return (
          <span style={{ fontSize: 15, fontWeight: 500, color: "#333" }}>
            {emailString}
          </span>
        );
      },
    },
  ];

  // Dynamic filter configuration for the slide-out drawer
  const filterConfig = [
    {
      type: "search",
      name: "search",
      label: "Search",
      placeholder: "Search users..."
    },
    {
      type: "status",
      name: "status",
      label: "Status",
      defaultValue: "all",
      options: [
        { label: "All Status", value: "all" },
        { label: "Active", value: "1" },
        { label: "Inactive", value: "0" }
      ]
    }
  ];

  // Actions configuration
  const actionConfig = {
    view: false,
    edit: hasPermission?.canEdit,
    delete: hasPermission?.canDelete,
    activate: hasPermission?.canEdit,
    deactivate: hasPermission?.canEdit,
    unblock: true, // Conditionally rendered in DataTable based on row.isBlocked
    minWidth: 220
  };

  // Bulk actions configuration
  const bulkActionConfig = {
    delete: hasPermission?.canDelete
  };

  const handleAction = (actionType, row) => {
    if (actionType === "edit") {
      handleAddUnitRate(null, row.id);
    } else if (actionType === "delete") {
      handleDeleteUser(null, row.id);
    } else if (actionType === "activate" || actionType === "deactivate") {
      changeMasterUserStatus(null, row);
    } else if (actionType === "unblock") {
      handleUnblockUser(null, row.id);
    }
  };

  return (
    <div id="kt_content_container">
      <BreadcrumbsComponent />

      <DataTable
        columns={columns}
        rows={masterUserData}
        loading={loading}
        totalCount={totalRecords}
        page={currentPage}
        pageSize={selectedRowsPerPage}
        filters={filterConfig}
        filterValues={filterValues}
        actions={actionConfig}
        bulkActions={bulkActionConfig}
        onFilterApply={(newValues) => {
          setFilterValues(newValues);
          setCurrentPage(1);
          setShouldFetchData(1);
        }}
        onFilterReset={() => {
          setFilterValues({ search: "", status: "all" });
          setCurrentPage(1);
          setShouldFetchData(1);
        }}
        onPageChange={(newPage) => {
          setCurrentPage(newPage);
          setShouldFetchData(1);
        }}
        onPageSizeChange={(newPageSize) => {
          setSelectedRowsPerPage(newPageSize);
          setCurrentPage(1);
          setShouldFetchData(1);
        }}
        onAction={handleAction}
        onBulkAction={handleBulkAction}
        addButtonText="ADD"
        onAddClick={(e) => handleAddUnitRate(e)}
        hasCreatePermission={hasPermission?.canCreate}
      />

      {openModal && (
        <AddEditMasterUserModal
          open={openModal}
          handleClose={handleCloseModal}
          masterUserDataById={masterUserDataById}
          setSnackbar={setSnackbar}
          roles={roles}
          merchantUser={merchantUser}
          setShouldFetchData={setShouldFetchData}
        />
      )}

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
    </div>
  );
};

export default MasterUserSection;
